<template>
  <div class="table-container" grid-drag-ignore>
    <table class="budget-table">
      <thead class="border-bottom">
        <tr>
          <th v-for="col in headers" :key="col.value" :class="getHeaderClass(col)">
            {{ col.headerFormatter ? col.headerFormatter(col.title) : col.title }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="processedData.length === 0">
          <td :colspan="headers.length" style="text-align: center">No data to display</td>
        </tr>
        <tr v-for="(row, rowIndex) in processedData" :key="rowIndex">
          <template v-for="col in headers">
            <td
              v-if="shouldRenderCell(row, col)"
              :key="col.value"
              :rowspan="getCellRowspan(row, col)"
              :class="getCellClass(col, rowIndex)"
              @click="onCellClick(rowIndex, col, row, $event)"
            >
              <div class="cell-content">
                <template v-if="edit && isEditable(col) && isEditing(rowIndex, col)">
                  <input
                    class="table-input-text cell-input"
                    :ref="inputRefKey(rowIndex, col)"
                    type="text"
                    v-model="editingValue"
                    inputmode="decimal"
                    @keydown.enter.prevent="commitEdit(rowIndex, col)"
                    @keydown.esc.prevent="cancelEdit"
                    @blur="cancelEdit"
                  />
                </template>

                <template v-else>
                  <v-tooltip v-if="isGrpAlertCell(col, rowIndex)" top content-class="grp-alert-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="16" class="mr-1 grp-alert-icon" v-bind="attrs" v-on="on">mdi-alert-circle</v-icon>
                    </template>
                    <span>Below 35 GRPs/week threshold</span>
                  </v-tooltip>
                  {{ getDisplayedValue(rowIndex, row, col) }}
                </template>
              </div>
            </td>
          </template>
        </tr>
      </tbody>
      <tfoot>
        <tr class="subtotal-row">
          <td :colspan="1" class="group-cell">Grand Total</td>
          <template v-for="col in headers.slice(1)">
            <td :key="col.value" :class="getFooterCellClass(col)">
              {{ isNumeric(col) ? getGrandTotalValue(col) : "" }}
            </td>
          </template>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";

import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import { isoKey, toDate, wStart } from "@/helpers/timeBuckets";
import planTableMetrics from "@/settings/metrics/planTableMetrics";
import { PlanInputDictionary } from "@/types/common";
import { ContentWidget } from "@/types/layouts";
import { Scenario as FullScenario, Project } from "@/types/project";

interface RowData {
  [key: string]: unknown;
}

const masterOrderMap = new Map<string, number>();
planTableMetrics.forEach((metric, index) => {
  masterOrderMap.set(metric.value, index);
});

interface Scenario {
  scenario: string;
  scenario_id: string;
  start_date: string;
  end_date: string;
  geography: string;
  rows: RowData[];
}

interface Plan {
  plan: string;
  children: Scenario[];
}

interface BusinessGoal {
  business_goal: string;
  campaign_id: string;
  children: Plan[];
}

interface ProcessedRow extends RowData {
  showBusinessGoal?: boolean;
  bgRowspan?: number;
  showPlan?: boolean;
  planRowspan?: number;
  showScenario?: boolean;
  scenarioRowspan?: number;
  __grpAlertWeeks?: Set<string>;
}
const VALIDATION_TOLERANCE = 0.01;
const NON_PERIOD_KEYS = new Set<string>(["channel_budget", "campaign_kpi", "plan", "geography"]);

export default Vue.extend({
  name: "ProjectOutputTimeTable",
  props: {
    data: {
      type: Array as PropType<BusinessGoal[]>,
      required: true,
    },
    headers: {
      type: Array as PropType<PlanInputDictionary[]>,
      required: true,
    },
    edit: Boolean,
    content: { type: Object as PropType<ContentWidget>, required: false },
  },

  data() {
    return {
      editingCell: { rowIndex: -1, colKey: "" as string },
      editingValue: "" as string,
      edits: {} as Record<string, number>,
      validationErrors: new Set<string>(),
    };
  },
  computed: {
    timeMode(): string {
      const mode = (this.content?.settings?.granularity as string) || "month";
      return ["week", "month", "quarter"].includes(mode) ? mode : "month";
    },
    processedData(): ProcessedRow[] {
      const processedRows: ProcessedRow[] = [];
      if (!this.data || !Array.isArray(this.data)) return [];

      this.data.forEach((bg) => {
        let firstOfBg = true;
        const bgGroupRows: ProcessedRow[] = [];

        bg.children?.forEach((plan) => {
          let firstOfPlan = true;
          const planGroupRows: ProcessedRow[] = [];

          plan.children?.forEach((scenario) => {
            const scenarioGroupRows: ProcessedRow[] = [];
            const scenarioId = String(scenario?.scenario_id ?? "");
            const fullProject = this.$store.getters["projectOutput/projectFiltered"];
            const fullScenario = scenarioId
              ? (this.findScenarioById(fullProject, scenarioId) as unknown as FullScenario)
              : undefined;

            const budgetInTime = fullScenario?.output?.budget_in_time;
            const grpAlerts = fullScenario?.output?.grp_threshold_alerts;

            const distByKey = new Map<string, Array<{ start_date: string; end_date: string; daily_budget: number }>>();
            if (Array.isArray(budgetInTime)) {
              for (const e of budgetInTime) {
                const key = this.safeKey(e?.kpi, e?.channel);
                if (!distByKey.has(key)) distByKey.set(key, Array.isArray(e?.distribution) ? e.distribution : []);
              }
            }

            const groupsByKpi = new Map<string, ProcessedRow[]>();

            scenario.rows.forEach((row) => {
              const kpi = String(row["campaign_kpi"] ?? "Other");

              const fullRow: ProcessedRow = {
                ...row,
                business_goal: bg.business_goal,
                campaign_id: bg.campaign_id,
                plan: plan.plan,
                geography: scenario.geography,
                campaign_kpi: kpi,
                scenario_id: scenario.scenario_id,
                start_date: scenario.start_date,
                end_date: scenario.end_date,
              };

              const rowAlerts = grpAlerts?.[kpi]?.[String(row["channel"] ?? "")] ?? [];
              fullRow.__grpAlertWeeks = new Set(
                rowAlerts
                  .map((alert) => {
                    const date = toDate(alert.start_date);
                    return date ? isoKey(wStart(date)) : null;
                  })
                  .filter((weekKey): weekKey is string => weekKey !== null)
              );

              if (!groupsByKpi.has(kpi)) {
                groupsByKpi.set(kpi, []);
              }
              groupsByKpi.get(kpi)!.push(fullRow);
            });

            const sortedKpis = Array.from(groupsByKpi.keys()).sort();
            let firstKpiInScenario = true;

            for (const kpi of sortedKpis) {
              const groupRows = groupsByKpi.get(kpi)!;

              for (const r of groupRows) {
                const rowKey = this.safeKey(String(r.campaign_kpi ?? ""), String(r.channel ?? ""));
                const dist = distByKey.get(rowKey) || [];

                if (this.timeMode === "month") {
                  this.headers.forEach((h) => {
                    if (h.lvl !== 2) return;
                    const key = String(h.value);
                    if (!this.isNumeric(h) || NON_PERIOD_KEYS.has(key)) return;
                    if (!/^\d{4}-\d{2}-01$/.test(key)) return;

                    const monthStart = key;
                    const monthEnd = this.monthEndStr(monthStart);

                    r[key] = this.monthSumFromDistribution(monthStart, monthEnd, dist);
                  });
                }
              }

              groupRows.forEach((row, i) => {
                row.showBusinessGoal = firstOfBg;
                row.showPlan = firstOfPlan;
                row.showScenario = firstKpiInScenario && i === 0;

                firstOfBg = false;
                firstOfPlan = false;
              });

              scenarioGroupRows.push(...groupRows);
              firstKpiInScenario = false;
            }

            if (scenarioGroupRows.length > 0) {
              scenarioGroupRows[0].scenarioRowspan = scenarioGroupRows.length;
              planGroupRows.push(...scenarioGroupRows);
            }
          });

          if (planGroupRows.length > 0) {
            planGroupRows[0].planRowspan = planGroupRows.length;
            bgGroupRows.push(...planGroupRows);
          }
        });

        if (bgGroupRows.length > 0) {
          bgGroupRows[0].bgRowspan = bgGroupRows.length;
          processedRows.push(...bgGroupRows);
        }
      });

      return processedRows;
    },
    hasPendingEdits(): boolean {
      return Object.keys(this.edits).length > 0;
    },
    canReset(): boolean {
      return this.hasPendingEdits || this.editingCell.rowIndex !== -1;
    },
    canRecalculate(): boolean {
      return this.hasPendingEdits && this.validationErrors.size === 0;
    },
    canEditConfig(): boolean {
      return Object.keys(this.edits).length === 0;
    },
  },
  watch: {
    canReset: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canResetChange", val);
      },
    },
    canRecalculate: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canRecalculateChange", val);
      },
    },
    canEditConfig: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canEditConfigChange", val);
      },
    },
  },

  methods: {
    normalizeNumber(v: unknown): number {
      if (typeof v === "number") return Number.isFinite(v) ? v : 0;
      if (typeof v === "string") {
        const cleaned = v.replace(/[^0-9.+-Ee]/g, "");
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : 0;
      }
      return 0;
    },
    safeKey(kpi?: string, channel?: string) {
      const norm = (v: unknown) =>
        String(v ?? "")
          .trim()
          .toLowerCase();
      return `${norm(kpi)}|${norm(channel)}`;
    },
    pad2(n: number) {
      return n < 10 ? `0${n}` : String(n);
    },
    addDaysYmd(ymd: string, days = 1): string {
      const [y, m, d] = ymd.split("-").map(Number);
      const t = Date.UTC(y, m - 1, d) + days * 86400000;
      const nd = new Date(t);
      return `${nd.getUTCFullYear()}-${this.pad2(nd.getUTCMonth() + 1)}-${this.pad2(nd.getUTCDate())}`;
    },
    findScenarioById(root: Project, wantedId: string): FullScenario | undefined {
      const stack: unknown[] = Array.isArray(root) ? [...root] : [root];

      while (stack.length) {
        const node = stack.pop();
        if (!node || typeof node !== "object") continue;

        const obj = node as Record<string, unknown>;
        const nodeId = String(obj["id"] ?? obj["scenario_id"] ?? "");
        if (nodeId && nodeId === wantedId) return obj as unknown as FullScenario;

        const childKeys = ["scenarios", "children", "plans", "plan_children", "variants", "rows"];
        for (const k of childKeys) {
          const v = obj[k];
          if (Array.isArray(v)) stack.push(...v);
        }

        for (const v of Object.values(obj)) {
          if (Array.isArray(v)) stack.push(...v);
          else if (v && typeof v === "object") stack.push(v);
        }
      }
      return undefined;
    },
    monthSumFromDistribution(
      monthStart: string,
      monthEnd: string,
      distribution: Array<{ start_date: string; end_date: string; daily_budget: number }>
    ): number {
      if (!Array.isArray(distribution) || distribution.length === 0) {
        return 0;
      }

      let sum = 0;
      for (let day = monthStart; day <= monthEnd; day = this.addDaysYmd(day, 1)) {
        let dayAdd = 0;
        for (const intv of distribution) {
          if (day >= intv.start_date && day <= intv.end_date) {
            dayAdd += Number(intv.daily_budget) || 0;
          }
        }
        sum += dayAdd;
      }

      return sum;
    },
    cellValidationKey(rowIndex: number, col: PlanInputDictionary): string {
      return `${rowIndex}:${String(col.value)}`;
    },
    getEffectiveCellValue(rowIndex: number, col: PlanInputDictionary): number {
      const key = this.cellKey(rowIndex, col);
      const raw = key in this.edits ? this.edits[key] : this.processedData[rowIndex][col.value];
      return this.normalizeNumber(raw);
    },
    validateRowTotals(rowIndex: number): void {
      const row = this.processedData[rowIndex];
      if (!row) return;

      const target = this.normalizeNumber(row.channel_budget);

      const periodCols = this.headers.filter(
        (h) => h.lvl === 2 && this.isNumeric(h) && !NON_PERIOD_KEYS.has(String(h.value))
      );

      const total = periodCols.reduce((acc, col) => acc + this.getEffectiveCellValue(rowIndex, col), 0);

      const diff = total - target;
      const ok = Math.abs(diff) <= VALIDATION_TOLERANCE;

      periodCols.forEach((col) => {
        this.validationErrors.delete(this.cellValidationKey(rowIndex, col));
      });

      if (!ok) {
        periodCols.forEach((col) => {
          const editKey = this.cellKey(rowIndex, col);
          if (editKey in this.edits) {
            this.validationErrors.add(this.cellValidationKey(rowIndex, col));
          }
        });
      }

      this.validationErrors = new Set(this.validationErrors);
    },

    applyFormatter(col: PlanInputDictionary, val: unknown): unknown {
      if (typeof col.formatter !== "function") return val;

      let fval: string | number | null;
      if (val === undefined || val === null) fval = null;
      else if (typeof val === "number" || typeof val === "string") fval = val;
      else {
        const asNum = Number(val);
        fval = Number.isFinite(asNum) ? asNum : String(val);
      }

      try {
        return col.formatter(fval);
      } catch (e) {
        console.error("[time-export] formatter error for", col.value, e);
        return val;
      }
    },
    getExportHeaderRow(): string[] {
      return this.headers.map((h) =>
        typeof h.headerFormatter === "function" ? String(h.headerFormatter(h.title)) : String(h.title)
      );
    },
    getExportCellValue(rowIndex: number, row: ProcessedRow, col: PlanInputDictionary): unknown {
      if (col.lvl === 0 && !row.showBusinessGoal) return "";
      if (col.lvl === 1) {
        if (col.value === "plan" && !row.showPlan) return "";
        if (col.value === "geography" && !row.showScenario) return "";
      }
      if (col.lvl !== 2) return row[col.value] ?? "";

      const key = this.cellKey(rowIndex, col);
      const raw = key in this.edits ? this.edits[key] : row[col.value];

      if (this.isNumeric(col)) {
        return this.applyFormatter(col, raw);
      }
      return typeof col.formatter === "function" ? this.applyFormatter(col, raw) : raw;
    },
    buildExportMatrix(): unknown[][] {
      const out: unknown[][] = [];

      out.push(this.getExportHeaderRow());

      this.processedData.forEach((row, rowIndex) => {
        const csvRow: unknown[] = [];
        this.headers.forEach((col) => {
          const val = this.getExportCellValue(rowIndex, row, col);
          csvRow.push(val);
        });
        out.push(csvRow);
      });

      if (this.processedData.length) {
        const totalRow: unknown[] = [];
        this.headers.forEach((col, idx) => {
          if (idx === 0) {
            totalRow.push("Grand Total");
            return;
          }
          if (!this.isNumeric(col)) {
            totalRow.push("");
            return;
          }
          const values = this.processedData.map((r) => Number(r[col.value]) || 0);
          let result: number;
          if (col.type === "average") {
            const valid = values.filter((v) => !isNaN(v));
            result = valid.length ? valid.reduce((s, v) => s + v, 0) / valid.length : 0;
          } else {
            result = values.reduce((s, v) => s + v, 0);
          }
          totalRow.push(this.applyFormatter(col, result));
        });
        out.push(totalRow);
      }

      return out;
    },
    exportProcessedData(): void {
      const title = "Budget Allocation";

      const csvContent: unknown[][] = [];
      csvContent.push(...buildHeaderRows(title));
      csvContent.push([]);
      csvContent.push(...this.buildExportMatrix());

      const fileName = buildFileName(title);
      csvExport(csvContent, fileName);
    },
    isEditable(col: PlanInputDictionary): boolean {
      return !!col.editable;
    },
    inputRefKey(rowIndex: number, col: PlanInputDictionary) {
      return `edit:${rowIndex}:${col.value}`;
    },
    cellKey(rowIndex: number, col: PlanInputDictionary) {
      return `${rowIndex}:${col.value}`;
    },
    isEditing(rowIndex: number, col: PlanInputDictionary) {
      return this.editingCell.rowIndex === rowIndex && this.editingCell.colKey === col.value;
    },
    onCellClick(rowIndex: number, col: PlanInputDictionary, row: ProcessedRow, e: MouseEvent) {
      if (!(this.edit && this.isEditable(col))) return;

      e.stopPropagation();
      this.editingCell = { rowIndex, colKey: col.value as string };

      const key = this.cellKey(rowIndex, col);
      const base = key in this.edits ? this.edits[key] : row[col.value];

      if (this.isNumeric(col)) {
        const num = Number(base) || 0;
        this.editingValue = num.toFixed(2);
      } else {
        this.editingValue = String(base ?? "");
      }

      this.$nextTick(() => {
        const ref = this.$refs[this.inputRefKey(rowIndex, col)] as HTMLInputElement | HTMLInputElement[] | undefined;
        const el = Array.isArray(ref) ? ref[0] : ref;
        el?.focus();
        el?.select();
      });
    },

    commitEdit(rowIndex: number, col: PlanInputDictionary) {
      const parsed = this.normalizeNumber(this.editingValue);

      if (!isNaN(parsed)) {
        this.$set(this.edits, this.cellKey(rowIndex, col), parsed);
      }

      this.validateRowTotals(rowIndex);

      this.editingCell = { rowIndex: -1, colKey: "" };
      this.editingValue = "";

      this.$emit("canResetChange", this.canReset);
      this.$emit("canRecalculateChange", this.canRecalculate);
    },

    cancelEdit() {
      this.editingCell = { rowIndex: -1, colKey: "" };
      this.editingValue = "";

      this.$emit("canResetChange", this.canReset);
      this.$emit("canRecalculateChange", this.canRecalculate);
    },
    monthEndStr(yyyyMm01: string): string {
      const [y, m] = yyyyMm01.split("-").map(Number);
      const d = new Date(Date.UTC(y, m, 0));
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${d.getUTCFullYear()}-${mm}-${dd}`;
    },
    daysInclusive(startYmd: string, endYmd: string): number {
      const [y1, m1, d1] = startYmd.split("-").map(Number);
      const [y2, m2, d2] = endYmd.split("-").map(Number);
      const t1 = Date.UTC(y1, m1 - 1, d1);
      const t2 = Date.UTC(y2, m2 - 1, d2);
      return Math.max(0, Math.floor((t2 - t1) / 86400000) + 1);
    },
    makeBudgetInTimePatchFromEdits() {
      const { edits } = this.getEditsPayload();

      const byKey = new Map<string, Array<Record<string, unknown>>>();
      for (const e of edits) {
        const key = `${e.scenario_id}||${e.campaign_kpi}||${e.channel}`;
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key)!.push(e);
      }

      const patches: Array<{
        scenario_id: string;
        kpi: string;
        channel: string;
        distribution: Array<{ start_date: string; end_date: string; daily_budget: number }>;
      }> = [];

      for (const [key, rows] of byKey.entries()) {
        const sample = rows[0];
        const scenStart = String(sample.start_date);
        const scenEnd = String(sample.end_date);

        rows.sort((a, b) => String(a.column).localeCompare(String(b.column)));

        const distribution: Array<{ start_date: string; end_date: string; daily_budget: number }> = [];

        for (const r of rows) {
          const periodStart = String(r.column);
          const periodEnd = this.periodEndStr(periodStart);

          const start_date = periodStart < scenStart ? scenStart : periodStart;
          const end_date = periodEnd > scenEnd ? scenEnd : periodEnd;

          const days = this.daysInclusive(start_date, end_date);
          const amountForPeriod = Number(r.value) || 0;

          if (days > 0 && amountForPeriod > 0) {
            const daily_budget = amountForPeriod / days;
            distribution.push({ start_date, end_date, daily_budget });
          }
        }

        const [scenario_id, kpi, channel] = key.split("||");
        patches.push({ scenario_id, kpi, channel, distribution });
      }

      return patches;
    },
    resetEdits() {
      this.cancelEdit();
      this.edits = {} as Record<string, number>;
      this.validationErrors.clear();
      this.validationErrors = new Set(this.validationErrors);
      this.$emit("canResetChange", this.canReset);
      this.$emit("canRecalculateChange", this.canRecalculate);
    },
    getEditsPayload() {
      const entries: Array<Record<string, unknown>> = [];
      Object.keys(this.edits).forEach((k) => {
        const [rowIndexStr, colVal] = k.split(":");
        const rowIndex = Number(rowIndexStr);
        const row = this.processedData[rowIndex];
        if (!row) return;

        entries.push({
          rowIndex,
          column: colVal,
          value: this.edits[k],
          business_goal: row.business_goal,
          campaign_id: row.campaign_id,
          plan: row.plan,
          geography: row.geography,
          campaign_kpi: row.campaign_kpi,
          channel: row.channel,
          scenario_id: row.scenario_id,
          start_date: row.start_date,
          end_date: row.end_date,
        });
      });
      return { edits: entries };
    },
    collectMonthCols(): string[] {
      return this.headers
        .filter(
          (h) =>
            h.lvl === 2 &&
            this.isNumeric(h) &&
            /^\d{4}-\d{2}-01$/.test(String(h.value)) &&
            !NON_PERIOD_KEYS.has(String(h.value))
        )
        .map((h) => String(h.value));
    },
    collectPeriodCols(): string[] {
      return this.headers
        .filter(
          (h) =>
            h.lvl === 2 &&
            this.isNumeric(h) &&
            /^\d{4}-\d{2}-\d{2}$/.test(String(h.value)) &&
            !NON_PERIOD_KEYS.has(String(h.value))
        )
        .map((h) => String(h.value));
    },
    periodEndStr(periodStartYmd: string): string {
      if (this.timeMode === "week") {
        return this.addDaysYmd(periodStartYmd, 6);
      }
      if (this.timeMode === "quarter") {
        const [y, m] = periodStartYmd.split("-").map(Number);
        const monthIdx = m - 1;
        const q = Math.floor(monthIdx / 3);
        const lastMonthIdx = q * 3 + 2;
        const d = new Date(Date.UTC(y, lastMonthIdx + 1, 0));
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const dd = String(d.getUTCDate()).padStart(2, "0");
        return `${d.getUTCFullYear()}-${mm}-${dd}`;
      }
      return this.monthEndStr(periodStartYmd);
    },
    fillGapsWithCurrentValues(): void {
      const periodCols = this.collectPeriodCols();

      this.processedData.forEach((row, rowIndex) => {
        periodCols.forEach((col) => {
          const key = `${rowIndex}:${col}`;
          if (!(key in this.edits)) {
            const v = Number(row[col]) || 0;
            if (v > 0) {
              this.$set(this.edits, key, v);
            }
          }
        });
      });
    },
    onClickPreparePayload() {
      this.fillGapsWithCurrentValues();

      const touchedRows = new Set<number>();
      Object.keys(this.edits).forEach((k) => {
        const [rowIndexStr] = k.split(":");
        const ri = Number(rowIndexStr);
        if (!Number.isNaN(ri)) touchedRows.add(ri);
      });
      touchedRows.forEach((ri) => this.validateRowTotals(ri));

      if (this.validationErrors.size > 0) {
        return;
      }

      const patches = this.makeBudgetInTimePatchFromEdits();

      const byScenario: Record<
        string,
        Array<{
          kpi: string;
          channel: string;
          distribution: Array<{ start_date: string; end_date: string; daily_budget: number }>;
        }>
      > = {};
      for (const p of patches) {
        if (!byScenario[p.scenario_id]) byScenario[p.scenario_id] = [];
        byScenario[p.scenario_id].push({ kpi: p.kpi, channel: p.channel, distribution: p.distribution });
      }

      this.$emit("onBudgetInTimeChange", patches);

      this.resetEdits();
    },
    getDisplayedValue(rowIndex: number, row: ProcessedRow, col: PlanInputDictionary): string | number {
      const key = this.cellKey(rowIndex, col);
      const rawValue = key in this.edits ? this.edits[key] : row[col.value];
      const raw = (rawValue ?? null) as string | number | null;

      if (col.formatter) {
        try {
          return this.isNumeric(col) ? col.formatter(raw, { notation: "compact" }) : col.formatter(raw);
        } catch {
          return raw ?? "";
        }
      }
      return raw ?? "";
    },
    getGrandTotalValue(col: PlanInputDictionary): string | number {
      if (col.value === "campaign_kpi") {
        return "Grand Total:";
      }

      if (!this.isNumeric(col)) {
        return "";
      }

      const values = this.processedData.map((r) => Number(r[col.value]) || 0);

      let result: number;
      if (col.type === "average") {
        const valid = values.filter((v) => !isNaN(v));
        result = valid.length ? valid.reduce((sum, v) => sum + v, 0) / valid.length : 0;
      } else {
        result = values.reduce((sum, v) => sum + v, 0);
      }

      return col.formatter ? col.formatter(result, { notation: "compact" }) : result;
    },
    shouldRenderCell(row: ProcessedRow, col: PlanInputDictionary): boolean {
      if (col.lvl === 0) return !!row.showBusinessGoal;
      if (col.lvl === 1) {
        if (col.value === "plan") return !!row.showPlan;
        if (col.value === "geography") return !!row.showScenario;
      }
      return col.lvl === 2;
    },
    getCellRowspan(row: ProcessedRow, col: PlanInputDictionary): number {
      if (col.lvl === 0) return row.bgRowspan || 1;
      if (col.value === "plan") return row.planRowspan || 1;
      if (col.value === "geography") return row.scenarioRowspan || 1;
      return 1;
    },
    isGrpAlertCell(col: PlanInputDictionary, rowIndex?: number): boolean {
      if (this.timeMode !== "week" || col.lvl !== 2 || typeof rowIndex !== "number") return false;
      const row = this.processedData[rowIndex];
      return !!row?.__grpAlertWeeks?.has(String(col.value));
    },
    getCellClass(col: PlanInputDictionary, rowIndex?: number): Record<string, boolean> {
      const hasError = typeof rowIndex === "number" && this.validationErrors.has(this.cellValidationKey(rowIndex, col));

      return {
        "group-cell": col.lvl < 2,
        "text-right": this.isNumeric(col),
        "border-right": col.lvl < 2,
        "border-bottom": col.lvl <= 2,
        "error-cell": !!hasError,
      };
    },
    getFooterCellClass(col: PlanInputDictionary): Record<string, boolean> {
      return {
        "group-cell": col.lvl < 2,
        "text-right": this.isNumeric(col),
        "border-bottom": col.lvl <= 2,
      };
    },
    getHeaderClass(col: PlanInputDictionary): Record<string, boolean> {
      return {
        "text-right": this.isNumeric(col),
      };
    },
    getCellValue(row: ProcessedRow, col: PlanInputDictionary): string | number {
      const value = (row[col.value] ?? "") as string | number | null;
      if (col.formatter) {
        return col.formatter(value, { notation: "compact" });
      }
      return value ?? "";
    },
    isNumeric(col: PlanInputDictionary): boolean {
      return col.tableGroup === "standard" || col.tableGroup === "calculated";
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/custom_variables";

.table-input-text {
  color: white !important;
  max-width: 100px;
}
.table-container {
  overflow-x: auto;
}
.budget-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  border-bottom: 1px solid #ffffff14;
}
.budget-table th,
.budget-table td {
  padding: 8px;
  text-align: left;
  white-space: nowrap;
  vertical-align: middle;
}
.budget-table th {
  font-weight: 600;
  top: 0;
}
.text-right {
  text-align: right;
}
.group-cell {
  font-weight: 500;
  vertical-align: top;
}
.border-right {
  border-right: 1px solid #ffffff14;
}

.border-bottom {
  border-bottom: 1px solid #ffffff14;
}
.subtotal-row {
  font-weight: bold;
  background: var(--v-background-medium-base) !important;

  &:hover {
    background: var(--v-background-medium-base) !important;
  }
}
.cell-content {
  position: relative;
  width: 100%;
}

.table-input-text {
  background: #151728;
  z-index: 9999;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 75px !important;
  max-width: 75px !important;
  box-sizing: border-box;
  color: white !important;
}

.v-application.theme--dark {
  .data-table {
    tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper):not(.subtotal-row) {
      background-color: unset !important;
    }
  }
}

.error-cell {
  background: transparent !important;
  outline: 2px solid #ff4d4f;
  outline-offset: -4px;
  border-radius: 8px;
}

.grp-alert-icon {
  opacity: 0.75;
  color: var(--v-error-base) !important;
  vertical-align: middle;
}
</style>

<style lang="scss">
.grp-alert-tooltip.v-tooltip__content {
  opacity: 1 !important;
  background: rgba(33, 33, 33, 0.98) !important;
}
</style>
